import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  CalendarDays, Users, ChevronLeft, ChevronRight,
  Ship, User, Phone, MapPin, Hotel, Package
} from "lucide-react";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import api from "@/lib/api";

function StatusBadge({ status }) {
  const s = (status || "").toUpperCase();
  let cls = "status-badge status-default";
  if (s === "CONFIRMED" || s === "ARRIVED") cls = "status-badge status-confirmed";
  else if (s === "CANCELLED") cls = "status-badge status-cancelled";
  else if (s === "RESERVED") cls = "status-badge status-reserved";
  return <span className={cls}>{status || "N/A"}</span>;
}

export default function Operations() {
  const navigate = useNavigate();
  const [date, setDate] = useState(new Date());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const dateStr = format(date, "yyyy-MM-dd");
  const isToday = dateStr === format(new Date(), "yyyy-MM-dd");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint = isToday ? "/operations/today" : `/operations/date/${dateStr}`;
      const result = await api.get(endpoint);
      setData(result.data);
    } catch (e) {
      console.error("Operations fetch error:", e);
    } finally {
      setLoading(false);
    }
  }, [dateStr, isToday]);

  useEffect(() => {
    fetchData();
    if (isToday) {
      const interval = setInterval(fetchData, 30000);
      return () => clearInterval(interval);
    }
  }, [fetchData, isToday]);

  const prevDay = () => {
    const d = new Date(date);
    d.setDate(d.getDate() - 1);
    setDate(d);
  };

  const nextDay = () => {
    const d = new Date(date);
    d.setDate(d.getDate() + 1);
    setDate(d);
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-white">Operasyon</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {isToday ? "Bugunun operasyonu" : format(date, "d MMMM yyyy", { locale: tr })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={prevDay} className="rounded-lg h-8 w-8 p-0 border-white/10 bg-white/[0.03] text-slate-400 hover:bg-white/[0.06]">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                data-testid="ops-date-picker"
                className="rounded-lg text-sm gap-1.5 font-normal border-white/10 bg-black/30 text-slate-300 hover:bg-white/[0.06]"
              >
                <CalendarDays className="w-3.5 h-3.5" />
                {format(date, "dd.MM.yyyy")}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar mode="single" selected={date} onSelect={(d) => d && setDate(d)} locale={tr} />
            </PopoverContent>
          </Popover>
          <Button variant="outline" size="sm" onClick={nextDay} className="rounded-lg h-8 w-8 p-0 border-white/10 bg-white/[0.03] text-slate-400 hover:bg-white/[0.06]">
            <ChevronRight className="w-4 h-4" />
          </Button>
          {!isToday && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDate(new Date())}
              className="rounded-lg text-xs border-white/10 bg-white/[0.03] text-slate-400 hover:bg-white/[0.06]"
            >
              Bugun
            </Button>
          )}
        </div>
      </div>

      <div className="page-body">
        {loading ? (
          <div className="space-y-4">
            <div className="grid grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="stat-card">
                  <div className="h-3 bg-zinc-200 animate-pulse w-20 rounded-sm mb-2" />
                  <div className="h-7 bg-zinc-200 animate-pulse w-12 rounded-sm" />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <>
            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="stat-card" data-testid="ops-total-bookings">
                <div className="stat-label">Toplam Rez.</div>
                <div className="stat-value">{data?.total_bookings || 0}</div>
              </div>
              <div className="stat-card" data-testid="ops-total-pax">
                <div className="stat-label">Toplam Yolcu</div>
                <div className="stat-value">{data?.total_pax || 0}</div>
              </div>
              <div className="stat-card" data-testid="ops-adult-count">
                <div className="stat-label">Yetiskin</div>
                <div className="stat-value">{data?.total_adult || 0}</div>
              </div>
              <div className="stat-card" data-testid="ops-child-count">
                <div className="stat-label">Cocuk / Bebek</div>
                <div className="stat-value">
                  {(data?.total_child || 0) + (data?.total_infant || 0)}
                </div>
              </div>
            </div>

            {/* Groups by Product */}
            {data?.groups && Object.entries(data.groups).length > 0 ? (
              Object.entries(data.groups).map(([productName, group]) => (
                <div key={productName} className="glass-card mb-4" data-testid={`ops-group-${productName.slice(0, 20)}`}>
                  {/* Group Header */}
                  <div className="flex items-center justify-between px-5 py-3 bg-white/[0.03] border-b border-white/[0.06]">
                    <div className="flex items-center gap-2">
                      <Ship className="w-4 h-4 text-blue-400" />
                      <span className="text-sm font-bold text-white">
                        {productName}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-xs">
                      <span className="font-medium text-slate-400">
                        {group.count} Rez.
                      </span>
                      <span className="font-bold text-blue-400">
                        {group.pax} Yolcu
                      </span>
                    </div>
                  </div>

                  {/* Bookings Table */}
                  <table className="w-full booking-table">
                    <thead>
                      <tr>
                        <th>Rez. No</th>
                        <th>Saat</th>
                        <th>Musteri</th>
                        <th>Telefon</th>
                        <th>Pax</th>
                        <th>Ekstralar</th>
                        <th>Otel</th>
                        <th>Notlar</th>
                        <th>Durum</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.bookings.map((b) => {
                        const bookingExtras = (data?.extras_map || {})[b.id] || [];
                        return (
                        <tr
                          key={b.id}
                          data-testid={`ops-booking-row-${b.id}`}
                          onClick={() => navigate(`/bookings/${b.id}`)}
                        >
                          <td>
                            <span className="font-mono text-xs text-blue-400 font-semibold">{b.confirmation_code || `#${b.bokun_booking_id}`}</span>
                          </td>
                          <td className="font-mono text-xs whitespace-nowrap">
                            {b.booking_time || "--:--"}
                          </td>
                          <td>
                            <div className="flex items-center gap-1.5">
                              <User className="w-3 h-3 text-slate-500" />
                              <span className="font-medium">{b.customer_name || "N/A"}</span>
                            </div>
                          </td>
                          <td>
                            <div className="flex items-center gap-1.5 text-xs">
                              <Phone className="w-3 h-3 text-slate-500" />
                              {b.customer_phone || "N/A"}
                            </div>
                          </td>
                          <td>
                            <span className="font-mono font-medium">{b.total_pax || 0}</span>
                            <span className="text-xs text-slate-500 ml-1">
                              ({b.adult_count || 0}A {b.child_count || 0}C {b.infant_count || 0}B)
                            </span>
                          </td>
                          <td>
                            {bookingExtras.length > 0 ? (
                              <div className="space-y-0.5">
                                {bookingExtras.map((ex, i) => (
                                  <div key={i} className="flex items-center gap-1 text-xs">
                                    <Package className="w-3 h-3 text-purple-400 shrink-0" />
                                    <span className="truncate max-w-[140px]">{ex.extra_name || "Extra"}</span>
                                    <span className="text-blue-400 font-mono font-semibold">x{ex.quantity}</span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span className="text-slate-600">-</span>
                            )}
                          </td>
                          <td>
                            {b.hotel_name ? (
                              <div className="flex items-center gap-1 text-xs">
                                <Hotel className="w-3 h-3 text-slate-500" />
                                <span className="truncate max-w-[120px]">{b.hotel_name}</span>
                              </div>
                            ) : (
                              <span className="text-slate-600">-</span>
                            )}
                          </td>
                          <td className="max-w-[150px] truncate text-xs text-slate-500">
                            {b.notes || "-"}
                          </td>
                          <td>
                            <StatusBadge status={b.booking_status} />
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ))
            ) : (
              <div className="glass-card p-12 text-center">
                <CalendarDays className="w-8 h-8 text-slate-600 mx-auto mb-3" />
                <div className="text-sm text-slate-500">Bu tarihte rezervasyon yok</div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
